import { useState, useEffect } from 'react'
import apiService from '../services/api'
import { useAuthStore } from '../store/authStore'

interface NotificationCount {
  totalUnread: number
  messageUnread: number
}

export function useNotifications() {
  const [notificationCounts, setNotificationCounts] = useState<NotificationCount>({
    totalUnread: 0,
    messageUnread: 0
  })
  const [loading, setLoading] = useState(false)
  const { user } = useAuthStore()

  const fetchNotificationCounts = async () => {
    if (!user) {
      setNotificationCounts({ totalUnread: 0, messageUnread: 0 })
      return
    }

    try {
      setLoading(true)

      // Fetch total unread count
      const totalResponse = await apiService.get('/api/notifications/unread-count')
      const totalUnread = (totalResponse as any).success
        ? (totalResponse as any).data.unreadCount
        : 0

      // Fetch message-specific unread count
      const messageResponse = await apiService.get('/api/notifications/unread-count?type=MESSAGE_RECEIVED')
      const messageUnread = (messageResponse as any).success
        ? (messageResponse as any).data.unreadCount
        : 0


      setNotificationCounts({
        totalUnread,
        messageUnread
      })
    } catch (error: any) {
      // Don't log errors for rate limiting to avoid console spam
      if (error.response?.status !== 429) {
        console.error('Error fetching notification counts:', error)
      }
      // Keep previous values on error to avoid flickering
    } finally {
      setLoading(false)
    }
  }

  // Fetch counts on mount and when user changes
  useEffect(() => {
    fetchNotificationCounts()

    if (user) {
      // Poll every 30 seconds to get faster updates
      const interval = setInterval(fetchNotificationCounts, 30000)
      
      // Listen for manual refresh events
      const handleRefresh = () => {
        fetchNotificationCounts()
      }
      window.addEventListener('refreshNotificationCounts', handleRefresh)
      
      return () => {
        clearInterval(interval)
        window.removeEventListener('refreshNotificationCounts', handleRefresh)
      }
    }
  }, [user])

  return {
    notificationCounts,
    loading,
    refetch: fetchNotificationCounts
  }
}